import {
  Component,
  Input,
  Output,
  EventEmitter,
  computed,
  signal,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { IconGlassComponent } from '../icon/icon-glass.component';
import { ButtonGlassComponent } from '../button/button-glass.component';
import { ButtonVariant } from '../models/button.model';
import { ModalVariant, ModalSize, ModalPosition } from '../models/modal.model';
import { LoggerService } from '../services/logger.service';

const modalVariants = cva({
  base: ['fixed inset-0 z-9999 flex items-center justify-center', 'transition-all duration-300 ease-out'].join(' '),
  variants: {
    position: {
      center: 'items-center',
      top: 'items-start pt-16',
      bottom: 'items-end pb-16',
    },
    visible: {
      true: 'opacity-100 pointer-events-auto',
      false: 'opacity-0 pointer-events-none',
    },
    size: {
      xs: 'p-4',
      sm: 'p-4',
      md: 'p-4',
      lg: 'p-4',
      xl: 'p-4',
      full: 'p-0', // No padding for fullscreen
    },
  },
  defaultVariants: {
    position: 'center',
    visible: 'false',
    size: 'md',
  },
});

const modalContentVariants = cva({
  base: ['relative z-10 w-full overflow-hidden', 'transition-all duration-300 ease-out transform-gpu'].join(' '),
  variants: {
    variant: {
      default: [
        'backdrop-blur-2xl border-2 rounded-xl shadow-2xl',
        'before:content-[""] before:absolute before:inset-0',
        'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
        'before:opacity-70 before:rounded-xl before:pointer-events-none',
        'before:transition-opacity before:duration-300',
        // Light theme
        'bg-linear-to-br from-slate-800/94 via-slate-900/95 to-blue-950/90',
        'border border-blue-200/16',
        'shadow-slate-950/42',
      ].join(' '),
      elevated: [
        'backdrop-blur-2xl border-2 rounded-xl shadow-2xl',
        'before:content-[""] before:absolute before:inset-0',
        'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
        'before:opacity-70 before:rounded-xl before:pointer-events-none',
        'before:transition-opacity before:duration-300',
        // Light theme
        'bg-linear-to-br from-slate-800/95 via-slate-900/96 to-blue-950/92',
        'border border-blue-200/18',
        'shadow-slate-950/44',
      ].join(' '),
      premium: [
        'backdrop-blur-2xl border-2 rounded-xl shadow-2xl',
        'before:content-[""] before:absolute before:inset-0',
        'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
        'before:opacity-70 before:rounded-xl before:pointer-events-none',
        'before:transition-opacity before:duration-300',
        // Ultra-premium for e-commerce
        'bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.14),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.08),transparent_42%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border border-[rgba(146,176,218,0.2)]',
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_24px_56px_rgba(2,8,23,0.42),0_0_48px_rgba(37,99,235,0.08)]',
      ].join(' '),
      minimal: [
        // Minimal variant with full size = fullscreen mode (no glass effects)
        'bg-transparent border-0 shadow-none',
        'backdrop-blur-0',
      ].join(' '),
      danger: [
        'backdrop-blur-2xl border-2 rounded-xl shadow-2xl',
        'before:content-[""] before:absolute before:inset-0',
        'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
        'before:opacity-70 before:rounded-xl before:pointer-events-none',
        'before:transition-opacity before:duration-300',
        'bg-[radial-gradient(circle_at_top_right,rgba(248,113,113,0.12),transparent_30%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border border-red-300/24',
        'shadow-slate-950/42',
      ].join(' '),
      success: [
        'backdrop-blur-2xl border-2 rounded-xl shadow-2xl',
        'before:content-[""] before:absolute before:inset-0',
        'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
        'before:opacity-70 before:rounded-xl before:pointer-events-none',
        'before:transition-opacity before:duration-300',
        'bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.12),transparent_30%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border border-emerald-300/24',
        'shadow-slate-950/42',
      ].join(' '),
      error: [
        'backdrop-blur-2xl border-2 rounded-xl shadow-2xl',
        'before:content-[""] before:absolute before:inset-0',
        'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
        'before:opacity-70 before:rounded-xl before:pointer-events-none',
        'before:transition-opacity before:duration-300',
        'bg-[radial-gradient(circle_at_top_right,rgba(248,113,113,0.12),transparent_30%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border border-red-300/24',
        'shadow-slate-950/42',
      ].join(' '),
      info: [
        'backdrop-blur-2xl border-2 rounded-xl shadow-2xl',
        'before:content-[""] before:absolute before:inset-0',
        'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
        'before:opacity-70 before:rounded-xl before:pointer-events-none',
        'before:transition-opacity before:duration-300',
        'bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.14),transparent_32%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border border-blue-300/24',
        'shadow-slate-950/42',
      ].join(' '),
      warning: [
        'backdrop-blur-2xl border-2 rounded-xl shadow-2xl',
        'before:content-[""] before:absolute before:inset-0',
        'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
        'before:opacity-70 before:rounded-xl before:pointer-events-none',
        'before:transition-opacity before:duration-300',
        'bg-[radial-gradient(circle_at_top_right,rgba(251,191,36,0.12),transparent_30%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border border-amber-300/24',
        'shadow-slate-950/42',
      ].join(' '),
    },
    size: {
      xs: 'max-w-xs max-h-[90vh]',
      sm: 'max-w-sm max-h-[90vh]',
      md: 'max-w-md max-h-[90vh]',
      lg: 'max-w-lg max-h-[90vh]',
      xl: 'max-w-2xl max-h-[90vh]',
      full: 'max-w-full max-h-full w-screen h-screen', // True fullscreen
    },
    visible: {
      true: 'scale-100 opacity-100',
      false: 'scale-95 opacity-0',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    visible: 'false',
  },
});

const backdropVariants = cva({
  base: [
    'fixed inset-0 z-0 transition-all duration-300 ease-out',
    'backdrop-blur-md',
    'pointer-events-none', // Don't capture clicks - let them go to parent container
  ].join(' '),
  variants: {
    variant: {
      default: 'bg-[linear-gradient(180deg,rgba(2,8,23,0.58)_0%,rgba(7,18,37,0.46)_100%)]',
      elevated: 'bg-[linear-gradient(180deg,rgba(2,8,23,0.62)_0%,rgba(7,18,37,0.5)_100%)]',
      premium:
        'bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.14),transparent_28%),linear-gradient(180deg,rgba(2,8,23,0.66)_0%,rgba(7,18,37,0.54)_100%)]',
      minimal: 'bg-[linear-gradient(180deg,rgba(2,8,23,0.52)_0%,rgba(7,18,37,0.42)_100%)]',
      danger:
        'bg-[radial-gradient(circle_at_top_right,rgba(248,113,113,0.12),transparent_28%),linear-gradient(180deg,rgba(2,8,23,0.64)_0%,rgba(7,18,37,0.52)_100%)]',
      success:
        'bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.12),transparent_28%),linear-gradient(180deg,rgba(2,8,23,0.64)_0%,rgba(7,18,37,0.52)_100%)]',
      error:
        'bg-[radial-gradient(circle_at_top_right,rgba(248,113,113,0.12),transparent_28%),linear-gradient(180deg,rgba(2,8,23,0.64)_0%,rgba(7,18,37,0.52)_100%)]',
      info: 'bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.12),transparent_28%),linear-gradient(180deg,rgba(2,8,23,0.64)_0%,rgba(7,18,37,0.52)_100%)]',
      warning:
        'bg-[radial-gradient(circle_at_top_right,rgba(251,191,36,0.12),transparent_28%),linear-gradient(180deg,rgba(2,8,23,0.64)_0%,rgba(7,18,37,0.52)_100%)]',
    },
    visible: {
      true: 'opacity-100',
      false: 'opacity-0',
    },
  },
  defaultVariants: {
    variant: 'default',
    visible: 'false',
  },
});

const headerVariants = cva({
  base: [
    'relative flex items-center justify-between p-6 border-b border-blue-200/10',
    'backdrop-blur-sm',
  ].join(' '),
  variants: {
    variant: {
      default: 'bg-white/4',
      elevated: 'bg-white/5',
      premium: 'bg-linear-to-r from-white/5 to-primary-500/8',
      minimal: 'bg-transparent',
      danger: 'bg-linear-to-r from-white/15 to-red-50/15 dark:from-white/10 dark:to-red-900/15',
      success: 'bg-linear-to-r from-white/15 to-cyan-50/15 dark:from-white/10 dark:to-cyan-900/15',
      error: 'bg-linear-to-r from-white/15 to-red-50/15 dark:from-white/10 dark:to-red-900/15',
      info: 'bg-linear-to-r from-white/15 to-blue-50/15 dark:from-white/10 dark:to-blue-900/15',
      warning: 'bg-linear-to-r from-white/15 to-amber-50/15 dark:from-white/10 dark:to-amber-900/15',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const contentVariants = cva({
  base: ['relative flex-1'].join(' '),
  variants: {
    variant: {
      default: '',
      elevated: '',
      premium: '',
      minimal: '',
      danger: '',
      success: '',
      error: '',
      info: '',
      warning: '',
    },
    size: {
      xs: 'p-6 max-h-[60vh] overflow-y-auto',
      sm: 'p-6 max-h-[60vh] overflow-y-auto',
      md: 'p-6 max-h-[60vh] overflow-y-auto',
      lg: 'p-6 max-h-[70vh] overflow-y-auto',
      xl: 'p-6 max-h-[80vh] overflow-y-auto',
      full: 'p-0 w-full h-full overflow-hidden', // No padding, no scroll, full size
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

const footerVariants = cva({
  base: [
    'relative flex items-center justify-end space-x-3 p-6 border-t border-blue-200/10',
    'backdrop-blur-sm',
  ].join(' '),
  variants: {
    variant: {
      default: 'bg-white/4',
      elevated: 'bg-white/5',
      premium: 'bg-linear-to-r from-primary-500/8 to-white/5',
      minimal: 'bg-transparent',
      danger: 'bg-linear-to-r from-red-50/15 to-white/15 dark:from-red-900/15 dark:to-white/10',
      success: 'bg-linear-to-r from-cyan-50/15 to-white/15 dark:from-cyan-900/15 dark:to-white/10',
      error: 'bg-linear-to-r from-red-50/15 to-white/15 dark:from-red-900/15 dark:to-white/10',
      info: 'bg-linear-to-r from-blue-50/15 to-white/15 dark:from-blue-900/15 dark:to-white/10',
      warning: 'bg-linear-to-r from-amber-50/15 to-white/15 dark:from-amber-900/15 dark:to-white/10',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

@Component({
  selector: 'ui-modal-glass',
  standalone: true,
  imports: [FormsModule, IconGlassComponent, ButtonGlassComponent],
  templateUrl: './modal-glass.component.html',
})
export class ModalGlassComponent implements OnInit, OnDestroy {
  // store the bound handler so removeEventListener can remove the exact same reference
  private readonly boundGlobalKeyDown = (event: KeyboardEvent): void => this.handleGlobalKeyDown(event);

  // store timeout id so we can clear it on destroy
  private focusTimeoutId: number | undefined;

  /** Selector for all focusable elements inside the modal. */
  private static readonly FOCUSABLE_SELECTOR = [
    'a[href]:not([disabled]):not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([disabled]):not([tabindex="-1"])',
  ].join(',');

  /** Saved body overflow value so we restore exactly what was there before. */
  private savedBodyOverflow: string | null = null;

  @ViewChild('modalContent') modalContent!: ElementRef;

  @Input() variant: ModalVariant = 'default';
  @Input() size: ModalSize = 'md';
  @Input() position: ModalPosition = 'center';
  @Input() title: string = '';
  @Input() subtitle: string = '';
  @Input() icon: string = '';
  @Input() hasHeader: boolean = true;
  @Input() hasFooter: boolean = true;
  @Input() showCloseButton: boolean = true;
  @Input() showCancelButton: boolean = true;
  @Input() showConfirmButton: boolean = true;
  @Input() cancelLabel: string = 'Cancel';
  @Input() confirmLabel: string = 'Confirm';
  @Input() closeOnBackdrop: boolean = true;
  @Input() closeOnEscape: boolean = true;
  @Input() hasBackdrop: boolean = true; // Show/hide backdrop
  @Input() hasCustomFooter: boolean = false;
  @Input() themeSection: ThemeSection = 'global';

  // Input field properties
  @Input() hasInput: boolean = false;
  @Input() inputLabel: string = '';
  @Input() inputPlaceholder: string = '';
  @Input() inputValue: string = '';
  @Input() inputRequired: boolean = false;
  @Input() hasProjectedContent: boolean = false; // Set to true if modal has content

  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() backdropClick = new EventEmitter<void>();
  @Output() inputValueChange = new EventEmitter<string>();

  protected isOpen = signal(false);
  protected inputModel: string = '';
  private previousActiveElement: HTMLElement | null = null;

  constructor(private readonly loggerService: LoggerService) {}

  protected computedModalClasses = computed(() => {
    return modalVariants({
      position: this.position,
      visible: this.isOpen() ? 'true' : 'false',
      size: this.size,
    });
  });

  protected computedContentClasses = computed(() => {
    return modalContentVariants({
      variant: this.variant,
      size: this.size,
      visible: this.isOpen() ? 'true' : 'false',
    });
  });

  protected computedBackdropClasses = computed(() => {
    return backdropVariants({
      variant: this.variant,
      visible: this.isOpen() ? 'true' : 'false',
    });
  });

  protected computedHeaderClasses = computed(() => {
    return headerVariants({
      variant: this.variant,
    });
  });

  protected computedModalContentClasses = computed(() => {
    return contentVariants({
      variant: this.variant,
      size: this.size,
    });
  });

  protected computedFooterClasses = computed(() => {
    return footerVariants({
      variant: this.variant,
    });
  });

  /** Resolve a ButtonVariant for the confirm button based on the modal variant. */
  protected getConfirmButtonVariant(): ButtonVariant {
    switch (this.variant) {
      case 'danger':
      case 'error':
        return 'danger';
      case 'success':
        return 'success';
      case 'minimal':
        return 'ghost';
      case 'info':
      case 'warning':
        return 'secondary';
      default:
        return 'primary';
    }
  }

  protected iconContainerClasses = computed(() => {
    const baseClasses = 'flex items-center justify-center w-12 h-12 rounded-lg backdrop-blur-sm';

    const variantClasses: Record<ModalVariant, string> = {
      default: 'bg-primary-500/20',
      elevated: 'bg-primary-500/25',
      premium: 'bg-linear-to-br from-primary-500/30 to-primary-600/20',
      minimal: 'bg-gray-500/20',
      danger: 'bg-red-500/20',
      success: 'bg-cyan-500/20',
      error: 'bg-red-500/20',
      info: 'bg-primary-500/20',
      warning: 'bg-primary-500/20',
    };

    return `${baseClasses} ${variantClasses[this.variant] ?? ''}`;
  });

  protected iconClasses = computed(() => {
    const variantClasses: Record<ModalVariant, string> = {
      default: 'text-primary-600 dark:text-primary-400',
      elevated: 'text-primary-600 dark:text-primary-400',
      premium: 'text-primary-700 dark:text-primary-300',
      minimal: 'text-gray-600 dark:text-gray-400',
      danger: 'text-red-600 dark:text-red-400',
      success: 'text-cyan-600 dark:text-cyan-400',
      error: 'text-red-600 dark:text-red-400',
      info: 'text-primary-600 dark:text-primary-400',
      warning: 'text-primary-600 dark:text-primary-400',
    };

    return variantClasses[this.variant] ?? '';
  });

  protected glowClasses = computed(() => {
    const variantClasses: Record<ModalVariant, string> = {
      default: '',
      elevated: '',
      premium: 'bg-primary-500/5 animate-glass-pulse',
      minimal: '',
      danger: 'bg-red-500/5 animate-glass-pulse',
      success: 'bg-cyan-500/5 animate-glass-pulse',
      error: 'bg-red-500/5 animate-glass-pulse',
      info: '',
      warning: '',
    };

    return `absolute inset-0 rounded-xl ${variantClasses[this.variant] ?? ''}`;
  });

  ngOnInit(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', this.boundGlobalKeyDown);
    }
  }

  ngOnDestroy(): void {
    if (typeof document !== 'undefined') {
      document.removeEventListener('keydown', this.boundGlobalKeyDown);
    }

    // clear pending focus timeout if any
    if (this.focusTimeoutId !== undefined) {
      clearTimeout(this.focusTimeoutId);
      this.focusTimeoutId = undefined;
    }

    // If modal was still open when destroyed, restore body scroll and focus
    if (this.isOpen()) {
      this.restoreBodyScroll();
    }
    this.restoreFocus();
  }

  open(): void {
    this.previousActiveElement = (
      typeof document !== 'undefined' ? document.activeElement : null
    ) as HTMLElement | null;
    this.inputModel = this.inputValue; // Initialize input with provided value
    this.isOpen.set(true);
    this.opened.emit();

    // Focus management - after the opening animation, move focus to the first
    // focusable element inside the modal (or the close button, or the container).
    this.focusTimeoutId = window.setTimeout(() => {
      this.focusFirstElement();
      this.focusTimeoutId = undefined;
    }, 300);

    // Prevent body scroll — save current value so we can restore exactly
    if (typeof document !== 'undefined' && document.body) {
      this.savedBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
  }

  close(): void {
    this.isOpen.set(false);
    this.closed.emit();
    this.restoreFocus();
    this.restoreBodyScroll();
  }

  confirm(): void {
    // Validate input if required
    if (this.hasInput && this.inputRequired && !this.inputModel.trim()) {
      return; // Don't confirm if required input is empty
    }

    // Emit input value if present
    if (this.hasInput) {
      this.inputValueChange.emit(this.inputModel);
    }

    this.confirmed.emit();
    this.close();
  }

  /**
   * Get the current input value
   */
  getInputValue(): string {
    return this.inputModel;
  }

  cancel(): void {
    this.cancelled.emit();
    this.close();
  }

  protected onBackdropClick(event: MouseEvent): void {
    this.loggerService.log('[glassUI] Modal backdrop clicked', {
      targetEqualsCurrentTarget: event.target === event.currentTarget,
      closeOnBackdrop: this.closeOnBackdrop,
      target: event.target,
      currentTarget: event.currentTarget,
    });

    if (event.target === event.currentTarget && this.closeOnBackdrop) {
      this.loggerService.log('[glassUI] Modal closing via backdrop click');
      this.backdropClick.emit();
      this.close();
    } else {
      this.loggerService.log('[glassUI] Modal backdrop click ignored');
    }
  }

  protected handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.closeOnEscape) {
      event.preventDefault();
      this.close();
    }
  }

  private handleGlobalKeyDown(event: KeyboardEvent): void {
    if (!this.isOpen()) {
      return;
    }

    if (event.key === 'Escape' && this.closeOnEscape) {
      this.close();
      return;
    }

    // Focus trapping: intercept Tab / Shift+Tab
    if (event.key === 'Tab') {
      this.handleFocusTrap(event);
    }
  }

  /**
   * Trap focus within the modal. When the user tabs past the last focusable
   * element, wrap to the first. When they shift-tab before the first, wrap to
   * the last.
   */
  private handleFocusTrap(event: KeyboardEvent): void {
    const container = this.modalContent?.nativeElement as HTMLElement | undefined;
    if (!container) {
      return;
    }

    const focusableElements = this.getFocusableElements(container);
    if (focusableElements.length === 0) {
      // Nothing to focus — prevent tabbing out of the modal entirely
      event.preventDefault();
      return;
    }

    const first = focusableElements[0] as HTMLElement | undefined;
    const last = focusableElements[focusableElements.length - 1] as HTMLElement | undefined;
    if (!first || !last) {
      event.preventDefault();
      return;
    }

    const active = typeof document !== 'undefined' ? document.activeElement : null;

    if (event.shiftKey) {
      // Shift+Tab: if focus is on the first element (or outside), wrap to last
      if (active === first || !container.contains(active as Node)) {
        event.preventDefault();
        last.focus();
      }
    } else {
      // Tab: if focus is on the last element (or outside), wrap to first
      if (active === last || !container.contains(active as Node)) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  /** Query all visible, enabled, focusable elements inside a container. */
  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const nodes = container.querySelectorAll<HTMLElement>(ModalGlassComponent.FOCUSABLE_SELECTOR);
    return Array.from(nodes).filter((el) => {
      // Skip elements hidden via CSS. offsetParent is null for hidden elements,
      // but also null in jsdom (no layout engine) and for position:fixed elements.
      // Use getClientRects as fallback; if both report "not visible" AND the element
      // has display:none/visibility:hidden explicitly set, exclude it.
      if (el.offsetParent !== null || el.getClientRects().length > 0) {
        return true;
      }
      // In environments without layout (e.g. SSR, jsdom), neither offsetParent nor
      // getClientRects work. Fall back to checking computed/inline display & visibility.
      const style = el.style;
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }
      // Cannot determine visibility without layout — assume visible
      return true;
    });
  }

  /** Move focus to the first focusable element inside the modal, or the container itself. */
  private focusFirstElement(): void {
    const container = this.modalContent?.nativeElement as HTMLElement | undefined;
    if (!container) {
      return;
    }

    try {
      const focusable = this.getFocusableElements(container);
      const firstFocusable = focusable[0] as HTMLElement | undefined;
      if (firstFocusable) {
        firstFocusable.focus();
      } else if (typeof container.focus === 'function') {
        container.focus();
      }
    } catch {
      // ignore focus failures in SSR or edge cases
    }
  }

  private restoreFocus(): void {
    if (this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
      try {
        this.previousActiveElement.focus();
      } catch {
        // swallow focus errors
      } finally {
        this.previousActiveElement = null;
      }
    }
  }

  /** Restore the body overflow style that was saved when the modal opened. */
  private restoreBodyScroll(): void {
    if (typeof document !== 'undefined' && document.body) {
      document.body.style.overflow = this.savedBodyOverflow ?? '';
      this.savedBodyOverflow = null;
    }
  }
}

/**
 * Usage Examples:
 *
 * Basic confirmation modal:
 * <ui-modal-glass
 *   #confirmModal
 *   title="Confirm Action"
 *   subtitle="Are you sure you want to proceed?"
 *   icon="alert-triangle"
 *   variant="default"
 *   (confirmed)="onConfirm()"
 *   (cancelled)="onCancel()"
 * >
 *   <p>This action cannot be undone.</p>
 * </ui-modal-glass>
 *
 * Premium e-commerce checkout modal:
 * <ui-modal-glass
 *   #checkoutModal
 *   title="Complete Your Order"
 *   subtitle="Review your items and shipping details"
 *   icon="shopping-cart"
 *   variant="premium"
 *   size="lg"
 *   [hasCustomFooter]="true"
 * >
 *   <div class="space-y-6">
 *     <!-- Order summary -->
 *     <ui-card-glass variant="subtle">
 *       <h3 class="font-semibold mb-4">Order Summary</h3>
 *       <!-- Cart items -->
 *     </ui-card-glass>
 *
 *     <!-- Payment form -->
 *     <ui-card-glass variant="subtle">
 *       <h3 class="font-semibold mb-4">Payment Details</h3>
 *       <!-- Payment form -->
 *     </ui-card-glass>
 *   </div>
 *
 *   <div slot="footer" class="flex justify-between items-center w-full">
 *     <span class="text-lg font-semibold">Total: $299.99</span>
 *     <div class="flex space-x-3">
 *       <ui-button-glass variant="ghost" (click)="checkoutModal.close()">Cancel</button>
 *       <ui-button-glass variant="primary" (click)="completeOrder()">Place Order</button>
 *     </div>
 *   </div>
 * </ui-modal-glass>
 *
 * Danger deletion modal:
 * <ui-modal-glass
 *   #deleteModal
 *   title="Delete Product"
 *   subtitle="This will permanently remove the product"
 *   icon="trash-2"
 *   variant="danger"
 *   confirmLabel="Delete"
 *   (confirmed)="deleteProduct()"
 * >
 *   <p class="text-gray-600 dark:text-gray-300">
 *     Are you sure you want to delete <strong>{{ productName }}</strong>?
 *     This action cannot be undone.
 *   </p>
 * </ui-modal-glass>
 *
 * Success notification modal:
 * <ui-modal-glass
 *   #successModal
 *   title="Order Placed Successfully"
 *   subtitle="Thank you for your purchase!"
 *   icon="check-circle"
 *   variant="success"
 *   [showCancelButton]="false"
 *   confirmLabel="View Order"
 * >
 *   <div class="text-center space-y-4">
 *     <p>Your order #12345 has been confirmed and will be shipped within 2-3 business days.</p>
 *     <ui-button-glass variant="ghost" (click)="trackOrder()">Track Your Order</button>
 *   </div>
 * </ui-modal-glass>
 *
 * Full-screen modal:
 * <ui-modal-glass
 *   #fullScreenModal
 *   title="Product Gallery"
 *   variant="minimal"
 *   size="full"
 *   [hasFooter]="false"
 * >
 *   <div class="grid grid-cols-2 md:grid-cols-3 gap-1">
 *     <!-- Product images -->
 *   </div>
 * </ui-modal-glass>
 *
 * Control from template:
 * <ui-button-glass (click)="confirmModal.open()">Show Confirmation</button>
 * <ui-button-glass (click)="checkoutModal.open()">Checkout</button>
 *
 * Different variants:
 * <ui-modal-glass variant="default" title="Default Modal"></ui-modal-glass>
 * <ui-modal-glass variant="elevated" title="Elevated Modal"></ui-modal-glass>
 * <ui-modal-glass variant="premium" title="Premium Modal"></ui-modal-glass>
 * <ui-modal-glass variant="danger" title="Danger Modal"></ui-modal-glass>
 * <ui-modal-glass variant="success" title="Success Modal"></ui-modal-glass>
 */
